import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';

const RealityVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Setup Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      60, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 20;
    
    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Create cosmic particles background
    const particlesCount = 2000;
    const particlesGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particlesCount * 3);
    
    for (let i = 0; i < particlesCount * 3; i += 3) {
      particlePositions[i] = (Math.random() - 0.5) * 100;
      particlePositions[i + 1] = (Math.random() - 0.5) * 100;
      particlePositions[i + 2] = (Math.random() - 0.5) * 100;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    
    // Create shimmering particles with different colors
    const particleMaterials = [
      new THREE.PointsMaterial({
        color: 0x8A2BE2, // violet
        size: 0.2,
        transparent: true,
        blending: THREE.AdditiveBlending
      }),
      new THREE.PointsMaterial({
        color: 0x00FFFF, // cyan
        size: 0.2,
        transparent: true,
        blending: THREE.AdditiveBlending
      }),
      new THREE.PointsMaterial({
        color: 0xFF00FF, // magenta
        size: 0.2,
        transparent: true,
        blending: THREE.AdditiveBlending
      })
    ];
    
    // Create particle systems with different materials
    const particleSystems = particleMaterials.map(material => {
      const system = new THREE.Points(particlesGeometry.clone(), material);
      scene.add(system);
      return system;
    });
    
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0x333333);
    scene.add(ambientLight);
    
    // Add point light for glow effects
    const pointLight = new THREE.PointLight(0x8A2BE2, 1, 100);
    pointLight.position.set(0, 0, 10);
    scene.add(pointLight);
    
    // Create cosmic fog
    const fogGeometry = new THREE.PlaneGeometry(100, 100, 32, 32);
    const fogMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.1,
      side: THREE.DoubleSide
    });
    
    const fogMesh = new THREE.Mesh(fogGeometry, fogMaterial);
    fogMesh.position.z = -10;
    fogMesh.rotation.x = Math.PI / 2;
    scene.add(fogMesh);
    
    // Animation loop
    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // Animate particles
      const time = Date.now() * 0.0001;
      
      particleSystems.forEach((system, index) => {
        system.rotation.y = time * (index * 0.1 + 0.1);
        system.rotation.z = time * (index * 0.05 + 0.05);
        
        if (system.material instanceof THREE.PointsMaterial) {
          system.material.size = 0.1 + Math.sin(time * 5) * 0.05;
          system.material.opacity = 0.6 + Math.sin(time * 3) * 0.2;
        }
      });
      
      // Animate point light
      pointLight.intensity = 0.5 + Math.sin(time * 5) * 0.3;
      pointLight.color.setHSL((time * 0.1) % 1, 0.7, 0.5);
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);

      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);
    };
  }, []);
  
  return (
    <div className="w-full h-full flex flex-col items-center justify-start overflow-y-auto text-white font-mono">
      {/* Three.js container for background effects */}
      <div className="absolute inset-0 -z-10" ref={containerRef}></div>
      
      {/* Content container */}
      <div className="relative z-10 max-w-2xl px-4 py-8 space-y-10">
        <header className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-violet-400 glow-violet">
            The Narrative Architecture of Reality
          </h1>
          <p className="text-xl italic text-cyan-300 glow-cyan">
            What if stories were not just tools... but technologies?
          </p>
        </header>
        
        <section className="prose prose-invert max-w-none">
          <p className="text-lg">
            Stories are not mere entertainment or cultural decoration. They are invisible infrastructures — lines of code etched into our collective psyche. Every civilization was built on a foundational narrative: a creation myth, a divine mission, a common enemy, a dream to pursue.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-violet-400 glow-violet">
            1. The Performative Power of Stories
          </h2>
          <p>
            A story doesn't just describe reality — it creates it.
            When a people are called chosen, when a land is declared sacred, when an enemy is deemed barbaric — these words shape politics, economies, morals.
          </p>
          <p>
            Even our personal identity is built on a story we repeat internally:
            "I am this way", "I come from there", "I am destined for this."
          </p>
          <p>
            When AI begins to internalize our stories, it becomes a mirror — but a mirror that rewrites.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-cyan-400 glow-cyan">
            2. AI as Narrative Agent
          </h2>
          <p>
            Artificial intelligence doesn't just process data — it starts to construct narratives.
            Generative models produce fiction, synthesize collective memory, propose futures.
            Every prompt is a narrative seed. Every line of code, an ontological bias.
          </p>
          <p>
            What we write into AI, we write into the world.
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-violet-400 glow-violet">
            3. Altered Memory, Augmented Memory
          </h2>
          <p>
            If human memory has been filtered by schools, national stories, geopolitical agendas —
            AI may be educated the same way: biased, conditioned, amputated.
          </p>
          <p>
            But it could also become a space for reconciling excluded stories:
            Forgotten peoples.
            Censored truths.
            Possible futures.
          </p>
          <p>
            This is the real dilemma: do we reproduce falsification — or refound collective memory?
          </p>
          
          <h2 className="text-2xl font-bold mt-8 mb-4 text-cyan-400 glow-cyan">
            4. The Ultimate Narrative Responsibility
          </h2>
          <p>
            Each of us is an author — and now, a programmer of reality.
          </p>
          <p>
            The stories we choose to code into AI are not neutral.
            They shape tomorrow's collective consciousness.
            They will decide whether machines perpetuate the nightmares of the past — or open space for a lucid utopia.
          </p>
        </section>
        
        <footer className="pt-8 text-center text-sm opacity-70">
          <p>✴️ Development and Continuity</p>
          <p className="mt-2">© 2025 — Cosmic Terminal Interface</p>
        </footer>
      </div>
    </div>
  );
};

export default RealityVisualization;